import { State, useSubscription } from '@meraki-internal/state';
import { useHistory } from 'react-router-dom';
import { IonButton, IonContent, IonHeader, IonLabel, IonModal, IonTitle, IonToggle, IonToolbar } from '@ionic/react';
import { isPlatform } from '../utils/isPlatform';
import { BackIcon, CloseIcon } from '../theme/Icons';
import { EnvConfiguration, IEnvironment } from '../config/EnvConfiguration';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { DevSettings, DevSettingsWriter } from './DevSettings';
import { useDevSettings } from '../utils/useDevSettings';
import { LiveUpdates, LiveUpdatesManager } from '@meraki-internal/live-updates';
import { useEffect, useState } from 'react';
import { AlertPresenter } from '../AlertBinder';
import { RevenueCatDebugPage } from '../innerhive-plus/revenue-cat/RevenueCatDebugPage';
import { WebSocketDebugPage } from '../web-sockets/WebSocketDebugPage';
import { RecordingDebugPage } from '../recording/RecordingDebugPage';
import { NotificationsDevTools } from '../push-notifications/NotificationsDevTools';
import { BeaChatDevTools } from '../helper-bee/BeaChatDevTools';
import { RecordingDevTools } from '../recording/RecordingDevTools';
import { DemoDataDevTools } from '../organizations/demo-data/DemoDataDevTools';
import { DEV_SETTINGS_METADATA, getSettingsBySection } from './DevSettingsMetadata';
import { IDevSettingsState } from './DevSettings';

type DebugPageTab = 'settings' | 'live-updates' | 'revenue-cat' | 'web-sockets' | 'recording';

export class DebugPageState extends State<{visibleTab?: DebugPageTab}> {
    constructor(){
        super({ visibleTab: undefined });
    }
    showSettings = () => {
        this.setState({ visibleTab: 'settings' });
    };

    showLiveUpdates = () => {
        this.setState({ visibleTab: 'live-updates' });
    };

    showRecording = () => {
        this.setState({ visibleTab: 'recording' });
    };

    hide = () => {
        this.setState({ visibleTab: undefined });
    };

    getVisibleTab = () => {
        return this.state?.visibleTab;
    };
}

type IEnvPlatformCondition = (params: { env: IEnvironment, isNative: boolean }) => boolean;

const enableCondition: {
    [settingKey: string]: IEnvPlatformCondition
} = {
    // require a device, and not local
    useStagingDeployChannel: ({ env, isNative }) => env !== 'local' && isNative,
    useStaging: ({ env, isNative }) => env !== 'local' && isNative,
};

export interface ITriggerOnboardingNotificationsResponse {
    success: boolean;
    scheduledCount: number;
}

const SettingsGroup: React.FC<{settings: DevSettings, groupKeys: (keyof IDevSettingsState)[], onChange: (key: string, value: boolean) => void}> = ({ settings, groupKeys, onChange }) => {
    const env = useInstance(EnvConfiguration).ENV;
    const isNative = isPlatform('capacitor');
    return (
        <ul style={{ listStyleType: 'none', paddingLeft: 20 }}>
            {groupKeys.map(settingKey => {
                const metadata = DEV_SETTINGS_METADATA[settingKey];
                return (
                    <li key={settingKey} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, marginLeft: 0}}>
                        <IonLabel title={metadata.description}>{metadata.name}</IonLabel>
                        <IonToggle
                            checked={(settings as any)[settingKey]}
                            onIonChange={e => onChange(settingKey, e.detail.checked) }
                            disabled={enableCondition[settingKey] && !enableCondition[settingKey]({ env, isNative })}
                        />
                    </li>
                );
            })}
        </ul>
    );
};

// Settings that require app reload due to initialization-time reads
const SETTINGS_REQUIRING_RELOAD = [
    'simulateIndexedDBFailure',
    'recordingShortPauseAutoSave'
];

const DevSettingsList = () => {
    const settings = useDevSettings();
    const settingsWriter = useInstance(DevSettingsWriter);
    const liveUpdatesService = useInstance(LiveUpdates);

    const alert = useInstance(AlertPresenter);

    const onChangeSetting = async (key: string, value: any) => {
        try {
            await settingsWriter.setSetting(key, value);

            if (key === 'useStaging') {
                // ensure the new env's live-update bundle is installed (or that we fall back
                // to native) on next app start, instead of continuing to run the previous
                // env's bundle. Stored env-neutrally inside the live-updates package.
                await liveUpdatesService.requireInstallOnNextAppStart();

                alert.showAlertV2({
                    header: 'Restart Manually',
                    message: 'Restart the app for the env change to take effect.',
                    options: [] // no options means alert can't be dismissed
                });
            } else if (SETTINGS_REQUIRING_RELOAD.includes(key)) {
                window.location.reload();
            }
            // No reload for other settings - they update reactively via State
        } catch (err: any) {
            // not logging, b/c this is internal only
            alert.showAlert({
                header: 'Failed',
                message: err.message
            });
        }
    };

    const generalKeys = getSettingsBySection('general');
    const recordingKeys = getSettingsBySection('recording');
    const taskKeys = getSettingsBySection('tasks');

    return (
        <>
            <SettingsGroup settings={settings} groupKeys={generalKeys} onChange={onChangeSetting} />

            <h5>Recording Dev Settings</h5>
            <SettingsGroup settings={settings} groupKeys={recordingKeys} onChange={onChangeSetting} />

            <h5>Task Dev Settings</h5>
            <SettingsGroup settings={settings} groupKeys={taskKeys} onChange={onChangeSetting} />
        </>
    );
};

export const DebugPage: React.FC = () => {
    const config = useInstance(EnvConfiguration);
    const liveUpdatesService = useInstance(LiveUpdates);

    const debugPageState = useInstance(DebugPageState);
    useSubscription(() => debugPageState);
    const history = useHistory();

    const [throwCount, setThrowCount] = useState<{ actualThrowCount: number, requestedThrowCount: number}>({ actualThrowCount: 0, requestedThrowCount: 0});
    const requestThrow = () => {
        setThrowCount(count => ({
            ...count,
            requestedThrowCount: count.requestedThrowCount + 1
        }));
    };

    useEffect(() => {
        if (throwCount.requestedThrowCount > throwCount.actualThrowCount){
            setThrowCount(count => ({
                ...count,
                actualThrowCount: count.actualThrowCount + 1
            }));
            throw new Error('triggering render error');
        }
    }, [setThrowCount, throwCount]);

    const visibleTab = debugPageState.getVisibleTab();

    return (
        <IonModal
            isOpen={visibleTab !== undefined}
            onDidDismiss={debugPageState.hide}
        >

            <IonHeader>
                <IonToolbar>
                    {visibleTab !== 'settings' &&
                        <IonButton slot="start" fill="clear" onClick={debugPageState.showSettings}><BackIcon /></IonButton>
                    }
                    <IonTitle>Debug Settings</IonTitle>
                    <IonButton slot="end" fill="clear" onClick={debugPageState.hide}><CloseIcon /></IonButton>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">

                {visibleTab === 'settings' &&
                    <>
                        <h5>Environment</h5>
                        <ul>
                            <li>env: {config.ENV}</li>
                            <li>web: {window.location.origin}</li>
                            <li>webSHA: {process.env.VITE_APP_SHA}</li>
                            <li>version: {liveUpdatesService.getActualVersion()}</li>
                            <li>api: {config.API_BASE_URL}</li>
                        </ul>

                        <h5>Dev Settings</h5>
                        <DevSettingsList />

                        <h5>Notification Dev Tools</h5>
                        <NotificationsDevTools />

                        <h5>Bea Chat Dev Tools</h5>
                        <BeaChatDevTools />

                        <h5>Recording Dev Tools</h5>
                        <RecordingDevTools />

                        <DemoDataDevTools />

                        <div style={{ display: 'flex', flexDirection: 'column', margin: '30px 0'}} >
                            <IonButton onClick={debugPageState.showLiveUpdates} disabled={!liveUpdatesService.showManagerLink()}>Live Updates</IonButton>
                            <IonButton onClick={() => debugPageState.setState({ visibleTab: 'recording' })}>Local Recordings</IonButton>
                            <IonButton onClick={() => { debugPageState.hide(); history.push('/feature-flags'); }}>Feature Flags</IonButton>
                            <IonButton onClick={() => { debugPageState.hide(); history.push('/admin/ai-logs'); }}>AI Call Logs</IonButton>
                            <IonButton onClick={() => debugPageState.setState({ visibleTab: 'revenue-cat' })}>Revenue Cat</IonButton>
                            <IonButton onClick={() => debugPageState.setState({ visibleTab: 'web-sockets' })}>Web Sockets</IonButton>
                            <IonButton fill="outline" onClick={() => {throw new Error('Test Sentry Error');}}>Test Sentry Error</IonButton>
                            <IonButton fill="outline" onClick={requestThrow}>Test Sentry Render Error</IonButton>
                        </div>
                    </>
                }

                {visibleTab === 'live-updates' &&
                    <LiveUpdatesManager />
                }
                {visibleTab === 'revenue-cat' &&
                    <RevenueCatDebugPage />
                }
                {visibleTab === 'web-sockets' &&
                    <WebSocketDebugPage />
                }

                {visibleTab === 'recording' &&
                    <RecordingDebugPage />
                }

            </IonContent>

        </IonModal>
    );
};
